import React, { useMemo } from 'react';
import {
  RISK_CATEGORY_DRAFT_OPTIONS,
  RISK_LEVEL_SELECT_OPTIONS,
  normalizeRiskCategoryForDraft,
  normalizeRiskLevelForDraft,
} from '../riskDraftSelectMaps.ts';
import {
  mergedRiskFieldReasons,
  riskDraftRowHasAnyMergedError,
  type RiskDraftTableField,
  type RiskDraftValidationRow,
} from '../riskDraftValidation.ts';
import { DraftReviewTable, type DraftColumn } from './DraftReviewTable.tsx';

export type RiskDraftReviewRow = RiskDraftValidationRow;

export type RiskDraftReviewTableProps = {
  rows: RiskDraftReviewRow[];
  selectedIds: Set<string>;
  onToggleAll?: (checked: boolean) => void;
  onToggleRow: (riskId: string, checked: boolean) => void;
  selectAllAriaLabel?: string;
  rowSelectAriaLabel: (row: RiskDraftReviewRow) => string;
  getFieldValue: (row: RiskDraftReviewRow, field: RiskDraftTableField) => unknown;
  isFieldEditing: (riskId: string, field: string) => boolean;
  startEditing: (riskId: string, field: string) => void;
  stopEditing: () => void;
  updateField: (riskId: string, field: string, value: string) => void;
  showBackendValidationErrors: boolean;
  applyFrontendValidation?: boolean;
  initialPageSize?: number;
  pageSizeOptions?: number[];
};

const RISK_COLUMNS: DraftColumn<RiskDraftTableField>[] = [
  {
    type: 'text',
    field: 'clientRiskId',
    header: 'Client Reference ID',
    shellClassName: 'w-[8rem] shrink-0',
    emptyLabel: 'Click to add reference ID',
  },
  {
    type: 'text',
    field: 'riskDescription',
    header: 'Risk description',
    shellClassName: 'w-full min-w-[10rem]',
    multiline: true,
    emptyLabel: 'Click to edit',
  },
  {
    type: 'select',
    field: 'riskLevel',
    header: 'Risk level',
    shellClassName: 'w-[12rem] shrink-0',
    options: RISK_LEVEL_SELECT_OPTIONS,
    normalizeValue: (v) => normalizeRiskLevelForDraft(v),
    selectPlaceholder: 'Select level',
  },
  {
    type: 'select',
    field: 'riskCategory',
    header: 'Risk category',
    shellClassName: 'w-[9rem] shrink-0',
    options: RISK_CATEGORY_DRAFT_OPTIONS,
    normalizeValue: (v) => normalizeRiskCategoryForDraft(v),
    selectPlaceholder: 'Select category',
  },
];

export function RiskDraftReviewTable({
  rows,
  selectedIds,
  onToggleAll,
  onToggleRow,
  selectAllAriaLabel,
  rowSelectAriaLabel,
  getFieldValue,
  isFieldEditing,
  startEditing,
  stopEditing,
  updateField,
  showBackendValidationErrors,
  applyFrontendValidation = true,
  initialPageSize = 10,
  pageSizeOptions,
}: RiskDraftReviewTableProps): React.ReactElement {
  const validationOpts = useMemo(
    () => ({
      showBackendErrors: showBackendValidationErrors,
      applyFrontendValidation,
    }),
    [showBackendValidationErrors, applyFrontendValidation]
  );

  return (
    <DraftReviewTable
      rows={rows}
      columns={RISK_COLUMNS}
      selectedIds={selectedIds}
      onToggleAll={onToggleAll}
      onToggleRow={onToggleRow}
      selectAllAriaLabel={selectAllAriaLabel}
      rowSelectAriaLabel={rowSelectAriaLabel}
      getFieldValue={getFieldValue}
      getFieldReasons={(row, field) => mergedRiskFieldReasons(row, field, getFieldValue, validationOpts)}
      rowHasError={(row) => riskDraftRowHasAnyMergedError(row, getFieldValue, validationOpts)}
      isFieldEditing={isFieldEditing}
      startEditing={startEditing}
      stopEditing={stopEditing}
      updateField={updateField}
      initialPageSize={initialPageSize}
      pageSizeOptions={pageSizeOptions}
    />
  );
}

export default RiskDraftReviewTable;
